import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import './NotificationSettingModal.scss'
import CustomButton from '../../UI/Buttons/CustomButton'
import GeneralForm from '../../Form'
import { FieldArray, FormikHelpers } from 'formik'
import { useDispatch, useSelector } from '../../../store'
import CustomPopup from '../../UI/Popup'
import TextInput from '../../UI/Inputs/TextInput'
import Checkbox from '../../UI/Inputs/Checkbox'
import RadioInput from '../../UI/Inputs/Radio/Simple'
import { toggleNotificationSettingModalAction } from '../../../store/application/slice'
import { TOAST_MESSAGE } from '../../UI/ToastNotification/ToastNotification'
import {
  getNotificationSettingsThunk,
  getEditNotificationSettingsThunk,
  updateNotificationSettingsThunk
} from '../../../store/notifications/thunks'
import { setNotificationIdAction } from '../../../store/notifications/slice'
import {
  TimeFrame,
  TimeFramePayload,
  Settings,
  SettingType,
  NotificationSettingPayload
} from '../../../store/notifications/types'
import SelectInput from '../../UI/Inputs/SelectInput'
import { SETTING_TYPE_ALIAS, WEEKS, INTERVAL_LIMIT } from '../../../models/notifications'
import { generateTimeInterval } from '../../../utils/transformerTImeHelper'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import { useToasts } from 'react-toast-notifications';
import { format, getUnixTime, parseISO, addDays,subDays } from 'date-fns'

interface NotificationSettingsModalProps {}

function NotificationSettingsModal(props: NotificationSettingsModalProps) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  let times = generateTimeInterval(30).map((v) => ({ label: v.hours_12, value: v.hours_24 }))
  times.unshift({ label: 'Select', value: '' })
  const [settings, setSettings] = useState<any>()

  const {
    inProgress,
    isOpenNotificationSetting,
    notificationId,
    settingType,
    editNotificationSetting,
    projectId
  } = useSelector((state) => ({
    inProgress: state.notifications.inProgress,
    isOpenNotificationSetting: state.app.isOpenNotificationSetting,
    notificationId: state.notifications.notificationId,
    settingType: state.notifications.settingType,
    editNotificationSetting: state.notifications.editNotificationSetting,
    projectId: state.project.currentProject.id
  }))

  useEffect(() => {
    if (notificationId) {
      const getEditNotificationSettings = async () => {
        const response = await dispatch(getEditNotificationSettingsThunk(notificationId))
      }
      getEditNotificationSettings()
    }
  }, [notificationId])

  useEffect(() => {
    if (editNotificationSetting) {
      setSettings(editNotificationSetting)
    }
  }, [editNotificationSetting])

  const onChangeSettingType = (alias_name: string, id: number) => {
    const value = settings
    setSettings({ ...value, timeframe_type_id: id })
  }

  const close = () => {
    setSettings(editNotificationSetting)
    dispatch(toggleNotificationSettingModalAction())
  }

  const setUpForm = {
    initialValues: {
      timeframe_type_id: settings ? settings.timeframe_type_id : 0,
      status: settings?.status,
      notify_timeframe: WEEKS.map((v) => {
        const interval: Array<any> = []
        new Array(INTERVAL_LIMIT).fill(1).forEach((vl, k) => {
          interval.push({
            id: settings?.notify_timeframe[v.id]?.[k]?.id,
            interval: settings?.notify_timeframe[v.id]?.[k]?.scheduled_time
          })
        })
        return {
          id: null,
          isChecked: settings?.notify_timeframe[v.id]?.length > 0 ? true : false,
          label: v.label,
          scheduled_day: v.id,
          scheduled_times: interval
        }
      })
    },
    enableReinitialize: true,
    validation: 'notification_settings',
    onSubmit: async (values: any, actions: FormikHelpers<any>) => {
      const notify_timeframe: Array<TimeFramePayload> = []
      values.notify_timeframe
        .filter((v: TimeFrame) => v.isChecked && v.scheduled_times)
        .forEach((v: TimeFrame) => {
          return v.scheduled_times.forEach((st: any) => {
            if (st.interval) {
              const currentDay = new Date().getDay()
              const actualDate = addDays(new Date,v.scheduled_day-currentDay)
              notify_timeframe.push({
                id: st.id ? st.id : 0,
                notification_id: notificationId,
                timestamp: getUnixTime(parseISO(format(actualDate, 'yyyy-MM-dd') +' '+ st.interval))
              })
            }
          })
        })

        if(values.timeframe_type_id === 3 && notify_timeframe.length === 0){
          addToast(TOAST_MESSAGE.NOTIFICATION_DAYS_MISSSING, { appearance: 'error' })
          return;
        }

      const payload: NotificationSettingPayload = {
        notification_id: notificationId,
        timeframe_type_id: settings.timeframe_type_id,
        status: 1,
        notify_timeframe: notify_timeframe,
        type_id:settings.type_id
      }
      const response = await dispatch(updateNotificationSettingsThunk(payload))
      if (isFulfilled(response)) {
        close()
        addToast(TOAST_MESSAGE.NOTIFICATION_UPDATED, { appearance: 'success' })
        await dispatch(setNotificationIdAction(0))
        await dispatch(getNotificationSettingsThunk(projectId))
      }
    }
  }

  return (
    <>
      <CustomPopup
        open={isOpenNotificationSetting}
        buttonClose={close}
        closeOnDocumentClick={false}
        className='notification-settings'
      >
        <div className='notification-settings__wrapper'>
          <div className='notification-settings__header'>Notification Settings</div>
          <p className='notification-settings__subtext'>Select when you’d like to be notified</p>
          <GeneralForm {...setUpForm}>
            {settingType.map(({ type_name, alias_name, id }) => (
              <div className='notification-settings__input'>
                <div className='notification-settings__radiolist'>
                  <RadioInput
                    key={`setting_type_${id}`}
                    handleChange={() => onChangeSettingType(alias_name, id)}
                    label={type_name}
                    checked={id === settings?.timeframe_type_id}
                    name={'timeframe_type_id'}
                  />
                </div>
              </div>
            ))}
            <p className='notification-settings__subtext'>
              Select which days and up to two times per day
            </p>
            {settings?.timeframe_type_id === 3 ? (
              <FieldArray
                name='notify_timeframe'
                render={({
                  push,
                  remove,
                  form: { values,errors, setFieldValue, ...formRest },
                  ...rest
                }) => (
                  <>
                    {values.notify_timeframe &&
                      values.notify_timeframe.map((timeframe: any, index: number) => (
                        <div key={index} className='notification-settings__input'>
                          <div className='notification-settings__timelist'>
                            <div className='notification-settings__checklist'>
                              <Checkbox
                                key={`week_${index}`}
                                label={timeframe.label}
                                name={`notify_timeframe.${index}.isChecked`}
                                checked={timeframe.isChecked}
                                onClick={({
                                  target: { checked }
                                }: React.ChangeEvent<HTMLInputElement>) =>
                                  setFieldValue(`notify_timeframe.${index}.isChecked`, checked)
                                }
                              />
                            </div>
                            {timeframe.isChecked === true ? (
                              <div className='notification-settings__selectlist'>
                                {values.notify_timeframe[index].scheduled_times &&
                                  values.notify_timeframe[index].scheduled_times.map(
                                    (intervals: any, intervalIndex: number) => (
                                      <div
                                        key={`interval_${index}_${intervalIndex}`}
                                        className='notification-settings__selectlistdrp'
                                      >
                                        <SelectInput
                                          label='numbers'
                                          name={`notify_timeframe.${index}.scheduled_times.${intervalIndex}.interval`}
                                          placeholder='Select'
                                          options={times}
                                        />
                                      </div>
                                    )
                                  )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {!!errors['notify_timeframe']?<div className='validation-slot-error'>
                      <div className='validation-slot-error__icon'></div>
                      Timeslots are missing
                      </div>:null}
                  </>
                )}
                
              />
            ) : null}

            <div className='notification-settings__actions'>
              <CustomButton onClick={close} type='button' button='secondary'>
                Cancel
              </CustomButton>
              <CustomButton inProgress={inProgress} button='primary' type='submit'>
                Save
              </CustomButton>
            </div>
          </GeneralForm>
        </div>
      </CustomPopup>
    </>
  )
}

export default NotificationSettingsModal
